import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton, useFocusTrap, type Tone, toneVars } from './primitives';
import { IconChevronLeft, IconChevronRight, IconClose, IconDownload } from './icons';
import { animateLightboxSpring, decideSwipe, velocityFromSamples } from './lightbox-swipe';
import { D_ENTER, D_EXIT, useOverlayMotion, usePrefersReducedMotion } from './motion';

// ---------------------------------------------------------------------------
// Modale / panneau glissant
// ---------------------------------------------------------------------------

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** `sheet` glisse depuis le bas (mobile), `dialog` est centré (bureau). */
  variant?: 'dialog' | 'sheet' | 'auto';
  width?: number | string;
  /** Empêche la fermeture par l'arrière-plan ou Échap (formulaire en cours). */
  dismissable?: boolean;
}

/**
 * Modale unique pour toute l'application. En dessous de 640 px de large, la
 * variante `auto` bascule en panneau glissant : c'est le comportement attendu
 * sur téléphone et cela évite deux composants à maintenir.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  variant = 'auto',
  width = 560,
  dismissable = true,
}: ModalProps) {
  const trapRef = useFocusTrap(open);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  const { visible, requestClose, duration } = useOverlayMotion(onClose);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissable) requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Bloquer le défilement de l'arrière-plan pendant l'ouverture.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, dismissable, requestClose]);

  if (!open || typeof document === 'undefined') return null;

  const asSheet = variant === 'sheet' || (variant === 'auto' && isNarrow);
  // Le panneau glissant sort par le bas, la modale centrée se rétracte sur
  // place : chacun repart par où il est arrivé.
  const hidden = asSheet ? 'translateY(100%)' : 'scale(0.94)';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal)' as unknown as number,
        display: 'flex',
        alignItems: asSheet ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: asSheet ? 0 : 'var(--s-4)',
        background: 'var(--scrim)',
        backdropFilter: 'var(--scrim-blur)',
        WebkitBackdropFilter: 'var(--scrim-blur)',
        opacity: visible ? 1 : 0,
        /*
         * Seule l'opacité est animée. Ajouter `backdrop-filter` à cette liste
         * empêchait Chromium de démarrer la transition : le voile passait de 1
         * à 0 sans image intermédiaire, alors que la règle et la valeur étaient
         * pourtant correctes (mesuré). Le flou suit donc l'opacité du voile,
         * ce qui suffit à ce qu'ils montent et descendent ensemble.
         */
        transition: `opacity ${duration}ms var(--ease-out)`,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && dismissable) requestClose();
      }}
    >
      <div
        ref={trapRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: asSheet ? '100%' : width,
          maxWidth: '100%',
          maxHeight: asSheet ? '92dvh' : '86dvh',
          background: 'var(--bg-elevated)',
          borderRadius: asSheet ? 'var(--r-2xl) var(--r-2xl) 0 0' : 'var(--r-xl)',
          boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
          opacity: visible ? 1 : 0,
          transform: visible ? 'none' : hidden,
          transition: `opacity ${duration}ms var(--ease-out), transform ${duration}ms var(--ease-out)`,
        }}
      >
        {asSheet && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 'var(--s-2)' }}>
            <span style={{ width: 36, height: 5, borderRadius: 'var(--r-xs)', background: 'var(--border-strong)' }} />
          </div>
        )}

        {(title || dismissable) && (
          <header
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--s-3)',
              padding: 'var(--s-4) var(--s-5)',
              borderBottom: '1px solid var(--separator)',
              flexShrink: 0,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && <h2 style={{ font: 'var(--t-title-3)' }}>{title}</h2>}
              {subtitle && (
                <p style={{ font: 'var(--t-footnote)', color: 'var(--text-secondary)', marginTop: 2 }}>
                  {subtitle}
                </p>
              )}
            </div>
            {dismissable && (
              <IconButton label="Fermer" onClick={requestClose} tone="gray">
                <svg width="14" height="14" viewBox="0 0 14 14">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </IconButton>
            )}
          </header>
        )}

        <div style={{ padding: 'var(--s-5)', overflowY: 'auto', flex: 1, minHeight: 0 }}>{children}</div>

        {footer && (
          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 'var(--s-2)',
              padding: 'var(--s-4) var(--s-5)',
              borderTop: '1px solid var(--separator)',
              background: 'var(--surface-2)',
              flexShrink: 0,
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Notifications éphémères
// ---------------------------------------------------------------------------

export interface Toast {
  id: string;
  message: string;
  tone: Tone;
  /** Durée d'affichage en ms ; `0` reste jusqu'à fermeture manuelle. */
  duration: number;
  action?: { label: string; onClick: () => void };
}

interface ToastApi {
  show: (message: string, options?: Partial<Omit<Toast, 'id' | 'message'>>) => string;
  success: (message: string) => string;
  error: (message: string) => string;
  info: (message: string) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast doit être utilisé dans un <ToastProvider>.');
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  /** Messages en cours de sortie : encore dans le DOM, déjà en train de partir. */
  const [leaving, setLeaving] = useState<string[]>([]);
  const reducedMotion = usePrefersReducedMotion();
  const exitDuration = reducedMotion ? 0 : D_EXIT;

  /*
   * Les minuteurs vivent dans une référence et non dans un `setTimeout` posé
   * puis oublié : il faut pouvoir les suspendre. Un message qui s'efface
   * pendant que l'onglet est en arrière-plan, ou pendant qu'on lit en le
   * survolant, n'a jamais été lu — c'est précisément le message d'erreur qu'on
   * perd de cette façon.
   */
  const timers = useRef(new Map<string, { restant: number; depuis: number; handle: number }>());
  const dismissRef = useRef<(id: string) => void>(() => {});

  const remove = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    setLeaving((current) => current.filter((value) => value !== id));
    timers.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      const timer = timers.current.get(id);
      if (timer) window.clearTimeout(timer.handle);
      timers.current.delete(id);
      setLeaving((current) => (current.includes(id) ? current : [...current, id]));
      window.setTimeout(() => remove(id), exitDuration);
    },
    [remove, exitDuration],
  );
  dismissRef.current = dismiss;

  const armer = useCallback((id: string, ms: number) => {
    timers.current.set(id, {
      restant: ms,
      depuis: Date.now(),
      handle: window.setTimeout(() => dismissRef.current(id), ms),
    });
  }, []);

  const suspendre = useCallback(() => {
    timers.current.forEach((timer) => {
      window.clearTimeout(timer.handle);
      timer.restant = Math.max(0, timer.restant - (Date.now() - timer.depuis));
    });
  }, []);

  const reprendre = useCallback(() => {
    timers.current.forEach((timer, id) => {
      timer.depuis = Date.now();
      timer.handle = window.setTimeout(() => dismissRef.current(id), timer.restant);
    });
  }, []);

  // Onglet masqué : on gèle tout, personne ne regarde.
  useEffect(() => {
    const onVisibility = () => (document.hidden ? suspendre() : reprendre());
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [suspendre, reprendre]);

  const show = useCallback<ToastApi['show']>(
    (message, options = {}) => {
      const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const toast: Toast = {
        id,
        message,
        tone: options.tone ?? 'accent',
        duration: options.duration ?? 4_000,
        action: options.action,
      };
      setToasts((current) => [...current.slice(-3), toast]);
      if (toast.duration > 0) armer(id, toast.duration);
      return id;
    },
    [armer],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message) => show(message, { tone: 'green' }),
      error: (message) => show(message, { tone: 'red', duration: 6_000 }),
      info: (message) => show(message, { tone: 'accent' }),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            aria-live="polite"
            // Survoler, c'est lire : le compte à rebours s'arrête tant que le
            // pointeur est sur la pile.
            onMouseEnter={suspendre}
            onMouseLeave={reprendre}
            style={{
              position: 'fixed',
              bottom: 'calc(var(--tabbar-h) + var(--s-4))',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--s-2)',
              zIndex: 'var(--z-toast)' as unknown as number,
              width: 'min(440px, calc(100vw - 32px))',
              pointerEvents: 'none',
            }}
          >
            {toasts.map((toast) => (
              <ToastRow
                key={toast.id}
                toast={toast}
                leaving={leaving.includes(toast.id)}
                onDismiss={() => dismiss(toast.id)}
              />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

/**
 * Un message éphémère.
 *
 * Composant à part et non simple `map` : chaque message a besoin de son propre
 * cycle de montage pour que son entrée s'anime, et d'une sortie propre. C'est
 * le seul élément de l'interface dont l'utilisateur est certain de regarder la
 * disparition, puisqu'elle se déclenche toute seule.
 */
function ToastRow({
  toast,
  leaving,
  onDismiss,
}: {
  toast: Toast;
  leaving: boolean;
  onDismiss: () => void;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const [shown, setShown] = useState(false);
  const { soft } = toneVars(toast.tone);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setShown(true));
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const visible = shown && !leaving;
  const duration = reducedMotion ? 0 : visible ? D_ENTER : D_EXIT;

  return (
    <div
      // Une erreur ne peut pas attendre le prochain silence du lecteur d'écran.
      role={toast.tone === 'red' ? 'alert' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-3)',
        padding: '11px var(--s-4)',
        borderRadius: 'var(--r-md)',
        // Un lavis de la teinte sur le verre, plutôt qu'un liseré de 3 px à
        // gauche : la même information, sans le trait rapporté.
        background: `linear-gradient(${soft}, ${soft}), var(--glass)`,
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-lg)',
        font: 'var(--t-subhead)',
        color: 'var(--text)',
        pointerEvents: 'auto',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(10px) scale(0.98)',
        transition: `opacity ${duration}ms var(--ease-out), transform ${duration}ms var(--ease-out)`,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{toast.message}</span>
      {toast.action && (
        <Button
          variant="plain"
          size="sm"
          tone={toast.tone}
          onClick={() => {
            toast.action?.onClick();
            onDismiss();
          }}
        >
          {toast.action.label}
        </Button>
      )}
      <IconButton label="Fermer" size="sm" tone="gray" onClick={onDismiss}>
        <svg width="11" height="11" viewBox="0 0 11 11">
          <path d="M1 1l9 9M10 1l-9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </IconButton>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

/**
 * Confirmation impérative : `const ok = await confirm({...})`. Évite de gérer un
 * état de modale dans chaque écran qui supprime quelque chose.
 */
export function useConfirm() {
  const [request, setRequest] = useState<
    (ConfirmOptions & { resolve: (value: boolean) => void }) | null
  >(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setRequest({ ...options, resolve });
      }),
    [],
  );

  const close = (value: boolean) => {
    request?.resolve(value);
    setRequest(null);
  };

  const dialog = request ? (
    <Modal
      open
      onClose={() => close(false)}
      title={request.title}
      width={420}
      variant="dialog"
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {request.cancelLabel ?? 'Annuler'}
          </Button>
          <Button variant={request.danger ? 'danger' : 'primary'} onClick={() => close(true)}>
            {request.confirmLabel ?? 'Confirmer'}
          </Button>
        </>
      }
    >
      <p style={{ font: 'var(--t-subhead)', color: 'var(--text-secondary)' }}>{request.message}</p>
    </Modal>
  ) : null;

  return { confirm, confirmDialog: dialog };
}

// ---------------------------------------------------------------------------
// Menu contextuel
// ---------------------------------------------------------------------------

export interface MenuItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Trait de séparation au-dessus de cet élément. */
  separated?: boolean;
}

export function Menu({
  items,
  trigger,
  align = 'right',
}: {
  items: MenuItem[];
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean }) => ReactNode;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  /**
   * Le panneau s'ouvre vers le bas, sauf s'il n'y a pas la place.
   *
   * Le menu du profil est en bas de la barre latérale : ouvert vers le bas, il
   * tombait sous le bord de la fenêtre. Le menu s'ouvrait bien, mais
   * « Se déconnecter » était hors de l'écran — il n'y avait donc aucun moyen de
   * se déconnecter de l'application.
   */
  const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
  /**
   * Même idée à l'horizontale : sur une ligne Baygon en dessous de 640 px, les
   * contrôles passent sous le nom et le déclencheur se retrouve à gauche. Avec
   * `align="right"` le panneau s'étend alors hors de l'écran — d'où un menu
   * coupé dont on ne lit que la fin des libellés.
   */
  const [edgeAlign, setEdgeAlign] = useState(align);
  const panelRef = useRef<HTMLDivElement | null>(null);

  /*
   * Trois états et non un seul :
   *   `open`    — l'intention de l'utilisateur ;
   *   `mounted` — la présence dans le DOM, maintenue le temps de la sortie ;
   *   `shown`   — l'état visuel, basculé une image après le montage.
   *
   * Sans `mounted`, React retire le nœud à la fermeture et il ne reste rien à
   * animer. Sans `shown`, le navigateur regroupe montage et état ouvert dans la
   * même image : il n'y a alors aucune valeur de départ à interpoler, et
   * l'entrée ne se joue pas.
   */
  const reducedMotion = usePrefersReducedMotion();
  const menuDuration = reducedMotion ? 0 : open ? D_ENTER : D_EXIT;
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setShown(true));
      });
      return () => cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), menuDuration);
    return () => window.clearTimeout(timer);
  }, [open, menuDuration]);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement('bottom');
      setEdgeAlign(align);
      return;
    }
    const panel = panelRef.current;
    if (!panel) return;
    // Mesuré après rendu plutôt qu'estimé : la hauteur dépend du nombre
    // d'entrées, et une estimation fausse rouvrirait le problème ailleurs.
    const rect = panel.getBoundingClientRect();
    const overflowsBelow = rect.bottom > window.innerHeight - 8;
    const roomAbove = rect.top - rect.height > 8;
    setPlacement(overflowsBelow && roomAbove ? 'top' : 'bottom');

    if (align === 'right' && rect.left < 8) setEdgeAlign('left');
    else if (align === 'left' && rect.right > window.innerWidth - 8) setEdgeAlign('right');
    else setEdgeAlign(align);
  }, [open, mounted, items.length, align]);

  useEffect(() => {
    if (!open) return;
    /**
     * Fermer dès qu'on interagit ailleurs — mais pas quand on clique dans le
     * menu lui-même.
     *
     * L'écoute est en capture : elle s'exécute avant les gestionnaires de la
     * cible. Sans cette exception, le menu se fermait avant que l'entrée
     * cliquée n'ait pu agir, et « Se déconnecter » ne faisait rien. Le
     * `stopPropagation` posé sur le panneau ne pouvait rien y faire : il est en
     * phase de bouillonnement, donc trop tard.
     */
    const close = (event: MouseEvent) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('click', close, { capture: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', close, { capture: true });
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  /*
   * Le panneau annonçait `role="menu"` sans en offrir la navigation : les
   * flèches faisaient défiler la page derrière, et il fallait tabuler jusqu'au
   * bout du menu pour en sortir. Le contrat d'un menu ARIA, c'est les flèches.
   *
   * Le focus est posé sur le premier élément dès l'ouverture, plutôt que suivi
   * dans un état : le DOM est déjà la source de vérité, et un index parallèle
   * se désynchronise dès qu'un élément est désactivé.
   */
  const focusItem = (delta: number) => {
    const buttons = Array.from(
      panelRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)') ?? [],
    );
    if (buttons.length === 0) return;
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    // Depuis le déclencheur (`current === -1`), Bas ouvre sur le premier et
    // Haut sur le dernier — la convention des menus natifs.
    const next = current < 0 ? (delta > 0 ? 0 : buttons.length - 1) : (current + delta + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  useEffect(() => {
    if (!shown) return;
    focusItem(1);
  }, [shown]);

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onKeyDown={(event) => {
        if (!open) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          focusItem(1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          focusItem(-1);
        } else if (event.key === 'Tab') {
          // Tabuler hors d'un menu ouvert le referme, comme partout ailleurs.
          setOpen(false);
        }
      }}
    >
      {trigger({
        onClick: () => setOpen((value) => !value),
        'aria-expanded': open,
      })}
      {mounted && (
        <div
          role="menu"
          ref={panelRef}
          onClick={(event) => event.stopPropagation()}
          style={{
            position: 'absolute',
            ...(placement === 'top' ? { bottom: 'calc(100% + 4px)' } : { top: 'calc(100% + 4px)' }),
            [edgeAlign]: 0,
            minWidth: 190,
            padding: 'var(--s-1)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 'var(--z-sheet)' as unknown as number,
            // Le panneau grandit depuis le coin par lequel il est ancré, et non
            // depuis son centre : c'est ce qui relie visuellement le menu à son
            // déclencheur. Les deux mesures nécessaires sont déjà calculées
            // au-dessus pour le placement.
            transformOrigin: `${placement === 'top' ? 'bottom' : 'top'} ${edgeAlign}`,
            opacity: shown ? 1 : 0,
            transform: shown ? 'none' : 'scale(0.94)',
            transition: `opacity ${menuDuration}ms var(--ease-out), transform ${menuDuration}ms var(--ease-out)`,
          }}
        >
          {items.map((item, index) => (
            <div key={item.label}>
              {item.separated && index > 0 && (
                <div style={{ height: 1, background: 'var(--separator)', margin: 'var(--s-1) 0' }} />
              )}
              <button
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--s-2)',
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 'var(--r-sm)',
                  font: 'var(--t-subhead)',
                  textAlign: 'left',
                  color: item.danger ? 'var(--red)' : 'var(--text)',
                  opacity: item.disabled ? 0.4 : 1,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(event) => {
                  if (!item.disabled) event.currentTarget.style.background = 'var(--surface-hover)';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = 'transparent';
                }}
              >
                {item.icon}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lightbox
// ---------------------------------------------------------------------------

export interface LightboxItem {
  id: string;
  src: string;
  alt?: string;
  /** Absent : pas de bouton de téléchargement pour cette image. */
  downloadUrl?: string;
  downloadName?: string;
}

/**
 * Même seuil que la modale : en dessous, l'écran est un téléphone et les
 * flèches latérales voleraient la place de la photo.
 */
const PHONE_MAX_WIDTH = 640;

/** Déplacement à partir duquel on distingue un glissé d'un tap. */
const SWIPE_SLOP_PX = 10;

/**
 * Visionneuse plein écran : agrandir une photo avant, éventuellement, de la
 * télécharger. Le bouton de téléchargement ne s'affiche que si l'appelant
 * fournit `downloadUrl` — c'est là que se respecte `allowDownload`.
 *
 * Sur un grand écran, les flèches et le clavier changent de photo. Sur
 * téléphone, le doigt fait glisser la photo : elle suit le geste, et la
 * voisine arrive avec lui.
 */
export function Lightbox({
  items,
  index,
  onClose,
  onIndexChange,
}: {
  items: LightboxItem[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}) {
  const trapRef = useFocusTrap(true);
  const item = items[index];
  const { visible, requestClose, duration } = useOverlayMotion(onClose);
  const reducedMotion = usePrefersReducedMotion();
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < PHONE_MAX_WIDTH,
  );

  const stageRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const imgRefs = useRef<Array<HTMLImageElement | null>>([null, null, null]);
  const offsetRef = useRef(0);
  const springStop = useRef<(() => void) | null>(null);
  const suppressClickRef = useRef(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origin: number;
    axis: 'undecided' | 'x' | 'y';
    samples: { x: number; t: number }[];
  } | null>(null);

  const indexRef = useRef(index);
  indexRef.current = index;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;
  const phoneRef = useRef(isPhone);
  phoneRef.current = isPhone;
  const reducedRef = useRef(reducedMotion);
  reducedRef.current = reducedMotion;

  const applyOffset = useCallback((px: number) => {
    offsetRef.current = px;
    const track = trackRef.current;
    if (!track) return;
    track.style.transform = `translate3d(calc(-100% / 3 + ${px}px), 0, 0)`;
  }, []);

  const paintSlides = useCallback((center: number) => {
    const list = itemsRef.current;
    const count = list.length;
    if (count < 2) return;
    const slots = [
      list[(center - 1 + count) % count]!,
      list[center]!,
      list[(center + 1) % count]!,
    ];
    imgRefs.current.forEach((img, slot) => {
      const photo = slots[slot];
      if (!img || !photo) return;
      if (img.getAttribute('src') !== photo.src) img.src = photo.src;
      img.alt = slot === 1 ? (photo.alt ?? '') : '';
    });
  }, []);

  const stopSpring = useCallback(() => {
    springStop.current?.();
    springStop.current = null;
  }, []);

  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < PHONE_MAX_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => () => stopSpring(), [stopSpring]);

  useLayoutEffect(() => {
    // Flèches et clavier : la nouvelle photo est déjà au centre, sans
    // animation. Un geste en cours garde la main sur le décalage.
    stopSpring();
    if (dragRef.current) return;
    applyOffset(0);
  }, [index, items.length, applyOffset, stopSpring]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose();
      else if (event.key === 'ArrowLeft' && items.length > 1) {
        onIndexChange((index - 1 + items.length) % items.length);
      } else if (event.key === 'ArrowRight' && items.length > 1) {
        onIndexChange((index + 1) % items.length);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [index, items.length, requestClose, onIndexChange]);

  const finishGesture = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.axis !== 'x') return;
    const stage = stageRef.current;
    const width = stage?.clientWidth ?? 0;
    const velocity = velocityFromSamples(drag.samples);
    const decision = decideSwipe(offsetRef.current, velocity, width);
    suppressClickRef.current = true;

    const settle = (target: number, done?: () => void) => {
      stopSpring();
      // Le doigt a déjà déplacé la photo. Avec « réduire les animations », on
      // pose le résultat tout de suite au lieu d'ajouter un glissement.
      if (reducedRef.current) {
        applyOffset(target);
        done?.();
        return;
      }
      springStop.current = animateLightboxSpring(
        offsetRef.current,
        target,
        velocity,
        applyOffset,
        () => {
          springStop.current = null;
          done?.();
        },
      );
    };

    if (decision === 0 || width <= 0) {
      settle(0);
      return;
    }

    const target = decision === 1 ? -width : width;
    settle(target, () => {
      const list = itemsRef.current;
      const count = list.length;
      if (count < 2) {
        applyOffset(0);
        return;
      }
      const nextIndex = (indexRef.current + decision + count) % count;
      // Même image, même cadre : on réécrit les sources puis on recentre
      // avant le prochain rendu, sinon la photo du milieu réapparaît une
      // image trop tôt.
      paintSlides(nextIndex);
      applyOffset(0);
      onIndexChangeRef.current(nextIndex);
    });
  }, [applyOffset, paintSlides, stopSpring]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (itemsRef.current.length < 2) return;
    if (event.pointerType === 'mouse' && !phoneRef.current) return;
    if (event.target instanceof Element && event.target.closest('button, a')) return;
    stopSpring();
    const stage = stageRef.current;
    stage?.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: offsetRef.current,
      axis: 'undecided',
      samples: [{ x: event.clientX, t: event.timeStamp }],
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || drag.axis === 'y') return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (drag.axis === 'undecided') {
      if (Math.abs(dx) < SWIPE_SLOP_PX && Math.abs(dy) < SWIPE_SLOP_PX) return;
      // Un geste surtout vertical n'est pas un changement de photo : on le
      // rend au navigateur plutôt que de coller l'image au doigt.
      if (Math.abs(dy) > Math.abs(dx)) {
        drag.axis = 'y';
        stageRef.current?.releasePointerCapture(event.pointerId);
        return;
      }
      drag.axis = 'x';
    }
    drag.samples.push({ x: event.clientX, t: event.timeStamp });
    if (drag.samples.length > 8) drag.samples.shift();
    applyOffset(drag.origin + dx);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.axis === 'x') {
      drag.samples.push({ x: event.clientX, t: event.timeStamp });
    }
    finishGesture();
  };

  if (!item || typeof document === 'undefined') return null;

  const count = items.length;
  const neighbors =
    count > 1
      ? [items[(index - 1 + count) % count]!, item, items[(index + 1) % count]!]
      : [];
  const showArrows = count > 1;
  // Sur téléphone les flèches restent dans l'arbre (clavier, lecteur d'écran)
  // mais sortent de la mise en page : le doigt a toute la photo.
  const navStyle = isPhone
    ? {
        position: 'absolute' as const,
        width: 1,
        height: 1,
        minWidth: 1,
        minHeight: 1,
        padding: 0,
        overflow: 'hidden' as const,
        clip: 'rect(0, 0, 0, 0)',
        pointerEvents: 'none' as const,
        color: '#fff',
      }
    : { color: '#fff', flexShrink: 0 };

  return createPortal(
    <div
      ref={trapRef}
      role="dialog"
      aria-modal="true"
      aria-label={item.alt || 'Photo'}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal)' as unknown as number,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--scrim-strong)',
        opacity: visible ? 1 : 0,
        transition: `opacity ${duration}ms var(--ease-out)`,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 'var(--s-3)' }}>
        <IconButton label="Fermer" onClick={requestClose} style={{ color: '#fff' }}>
          <IconClose size={22} />
        </IconButton>
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--s-3)',
          minHeight: 0,
          padding: '0 var(--s-3)',
        }}
        onClick={(event) => {
          // Clic sur le scrim autour de la photo (pas sur l’image ni les flèches).
          if (event.target === event.currentTarget) requestClose();
        }}
      >
        {showArrows && (
          <IconButton
            label="Photo précédente"
            className={isPhone ? 'bala-sr-only' : undefined}
            onClick={(event) => {
              event.stopPropagation();
              onIndexChange((index - 1 + count) % count);
            }}
            style={navStyle}
          >
            <IconChevronLeft size={24} />
          </IconButton>
        )}

        {count < 2 ? (
          <img
            src={item.src}
            alt={item.alt ?? ''}
            draggable={false}
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 'var(--r-md)' }}
            onClick={(event) => event.stopPropagation()}
          />
        ) : (
          <div
            ref={stageRef}
            style={{
              position: 'relative',
              flex: '1 1 auto',
              alignSelf: 'stretch',
              height: '100%',
              minWidth: 0,
              overflow: 'hidden',
              touchAction: 'none',
              userSelect: 'none',
              cursor: isPhone ? 'grab' : undefined,
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onLostPointerCapture={onPointerUp}
            onContextMenu={(event) => event.preventDefault()}
            onClickCapture={(event) => {
              if (!suppressClickRef.current) return;
              suppressClickRef.current = false;
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {isPhone && <p className="bala-sr-only">Balayez horizontalement pour changer de photo.</p>}
            <div
              ref={trackRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '300%',
                display: 'flex',
                willChange: 'transform',
              }}
            >
              {neighbors.map((photo, slot) => (
                <div
                  key={slot}
                  style={{
                    flex: '0 0 calc(100% / 3)',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 0,
                  }}
                  onClick={(event) => {
                    if (event.target === event.currentTarget) requestClose();
                  }}
                >
                  <img
                    ref={(node) => {
                      imgRefs.current[slot] = node;
                    }}
                    src={photo.src}
                    alt={slot === 1 ? (photo.alt ?? '') : ''}
                    aria-hidden={slot === 1 ? undefined : true}
                    draggable={false}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      minWidth: 0,
                      minHeight: 0,
                      objectFit: 'contain',
                      borderRadius: 'var(--r-md)',
                      userSelect: 'none',
                    }}
                    onClick={(event) => event.stopPropagation()}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {showArrows && (
          <IconButton
            label="Photo suivante"
            className={isPhone ? 'bala-sr-only' : undefined}
            onClick={(event) => {
              event.stopPropagation();
              onIndexChange((index + 1) % count);
            }}
            style={navStyle}
          >
            <IconChevronRight size={24} />
          </IconButton>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--s-3)',
          padding: 'var(--s-4)',
        }}
      >
        {count > 1 && (
          <span aria-live="polite" aria-atomic="true" style={{ font: 'var(--t-footnote)', color: 'rgb(255 255 255 / 0.7)' }}>
            {index + 1} / {count}
          </span>
        )}
        {item.downloadUrl && (
          <a
            href={item.downloadUrl}
            download={item.downloadName ?? true}
            onClick={(event) => event.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--s-2)',
              height: 36,
              padding: '0 14px',
              borderRadius: 'var(--r-md)',
              background: 'var(--surface-2)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              font: 'var(--t-subhead)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            <IconDownload size={15} />
            Télécharger
          </a>
        )}
      </div>
    </div>,
    document.body,
  );
}
